import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PlayingCard from './PlayingCard';
import {
  cardLabel, fanLayout, formatGram, parseCard, SUIT_SYMBOL,
} from '../utils/cards';
import { haptic, hapticSelect } from '../../brawl/utils/haptics';
import { Trans, useTranslation } from 'react-i18next';
import { gameErrorText } from '../../common/gameErrors';

/**
 * The Durak table (portrait). Pure view of `durak.state` + the live table from events; every tap is turned into
 * a `durak.*` message through `send`. Nothing here decides legality — the server's `legal` / `beatTargets` /
 * `canTake` / `canDone` drive highlighting and buttons.
 */

const FX_MS = 600;

function useNow(active, everyMs = 200) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [active, everyMs]);
  return now;
}

export function DeadlineRing({ deadlineAt, totalMs, mine }) {
  const { t } = useTranslation();
  const now = useNow(!!deadlineAt);
  const left = Math.max(0, (deadlineAt || 0) - now);
  const frac = totalMs > 0 ? Math.min(1, left / totalMs) : 0;
  const r = 15;
  const len = 2 * Math.PI * r;
  const urgent = left < 8000;
  return (
    <span className={`dk-ring${mine ? ' dk-ring--mine' : ''}${urgent ? ' is-urgent' : ''}`} aria-label={t('durak.seconds_left', '{{n}} seconds left', { n: Math.ceil(left / 1000) })}>
      <svg viewBox="0 0 36 36">
        <circle cx="18" cy="18" r={r} className="dk-ring__bg" />
        <circle
          cx="18"
          cy="18"
          r={r}
          className="dk-ring__fg"
          strokeDasharray={len}
          strokeDashoffset={len * (1 - frac)}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <b>{Math.ceil(left / 1000)}</b>
    </span>
  );
}

/**
 * Server-side бито pause (`phase === 'BITO'`, DurakHub.BITO_PAUSE_MS): the beaten table stays put for both players
 * and nobody can move; a small "Бито" badge with a countdown sits over the cards until the discard.
 */
export function BitoBadge({ endsAt, totalMs }) {
  const { t } = useTranslation();
  const now = useNow(!!endsAt, 100);
  const left = Math.max(0, (endsAt || 0) - now);
  const frac = totalMs > 0 ? Math.min(1, left / totalMs) : 0;
  return (
    <div className="dk-bito" data-testid="bito-pause" role="status" aria-label={t('durak.bito_aria', 'Бито — cards go to the discard pile')}>
      <span className="dk-bito__label">{t('durak.bito', 'Бито')}</span>
      <span className="dk-bito__sec">{Math.ceil(left / 1000)}</span>
      <span className="dk-bito__bar"><span style={{ transform: `scaleX(${frac})` }} /></span>
    </div>
  );
}

function OpponentFan({ count }) {
  const shown = Math.min(count, 12);
  const layout = fanLayout(shown, {
    width: 190, cardW: 42, maxStep: 18, maxAngle: 5,
  });
  return (
    <div className="dk-opp-fan" data-testid="opp-fan">
      {layout.map((p, i) => (
        <div
          key={i}
          className="dk-opp-card"
          style={{ transform: `translateX(${p.x}px) translateY(${-p.y}px) rotate(${-p.rot}deg)` }}
        >
          <PlayingCard faceDown />
        </div>
      ))}
      <span className="dk-opp-count">{count}</span>
    </div>
  );
}

function Deck({ deckCount, trump, trumpSuit, pulseKey }) {
  const { t } = useTranslation();
  const trumpCard = parseCard(trump);
  return (
    <div className="dk-deck" aria-label={t('durak.deck_aria', 'Deck: {{n}} cards, trump {{suit}}', { n: deckCount, suit: trumpSuit })}>
      {deckCount > 0 ? (
        <>
          <div className="dk-deck__trump"><PlayingCard code={trump} /></div>
          {deckCount > 1 && (
            <div key={pulseKey} className="dk-deck__stack">
              {deckCount > 8 && <PlayingCard faceDown className="dk-deck__layer dk-deck__layer--2" />}
              {deckCount > 3 && <PlayingCard faceDown className="dk-deck__layer dk-deck__layer--1" />}
              <PlayingCard faceDown className="dk-deck__layer" />
            </div>
          )}
        </>
      ) : (
        <div className={`dk-deck__empty${trumpCard?.red ? ' is-red' : ''}`}>{SUIT_SYMBOL[trumpSuit]}</div>
      )}
      <span className="dk-deck__count">{deckCount}</span>
    </div>
  );
}

function Discard({ count }) {
  const { t } = useTranslation();
  if (!count) return <div className="dk-discard" />;
  const layers = Math.min(4, Math.ceil(count / 6));
  return (
    <div className="dk-discard" aria-label={t('durak.discard_aria', 'Discard pile: {{n}} cards', { n: count })}>
      {Array.from({ length: layers }, (_, i) => (
        <PlayingCard key={i} faceDown className="dk-discard__card" style={{ transform: `rotate(${[-14, 9, -4, 17][i]}deg)` }} />
      ))}
      <span className="dk-discard__label">{t('durak.bito', 'Бито')} {count}</span>
    </div>
  );
}

function statusText(t, st, you) {
  const meAttack = st.attacker === you;
  const myTurn = st.turn === you;
  if (st.phase === 'OVER') return t('durak.status.over', 'Game over');
  if (st.phase === 'BITO') return t('durak.status.bito', 'Бито — all cards beaten');
  if (myTurn) {
    if (st.phase === 'ATTACK') return t('durak.status.your_attack', 'Your attack — play any card');
    if (st.phase === 'DEFEND') return t('durak.status.defend', 'Defend! Beat the cards or take');
    if (st.taking) return t('durak.status.opp_takes', 'Opponent takes — throw in more or press Done');
    return t('durak.status.all_beaten', 'All beaten — throw in or Бито');
  }
  if (st.phase === 'DEFEND') return t('durak.status.opp_defending', 'Opponent is defending…');
  if (meAttack) return t('durak.status.opp_thinking', 'Opponent is thinking…');
  return st.taking
    ? t('durak.status.you_take', 'You take — opponent may throw in more…')
    : t('durak.status.opp_attacking', 'Opponent is attacking…');
}

export default function DurakTable({
  match, state, table: liveTable, fx, send, onSurrender, connection, error,
}) {
  const { t } = useTranslation();
  const you = state.you;
  const myTurn = state.turn === you;
  const legal = useMemo(() => new Set(state.legal || []), [state.legal]);
  const beatTargets = state.beatTargets || {};
  const defending = myTurn && state.phase === 'DEFEND';
  const attacking = myTurn && (state.phase === 'ATTACK' || state.phase === 'THROW_IN');
  const table = liveTable || state.table || [];
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(null);
  const [ghost, setGhost] = useState(null);
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? Math.min(window.innerWidth, 480) : 380));
  const turnKey = `${state.v}-${state.turn}`;
  const prevMyTurn = useRef(myTurn);

  // a new state invalidates any half-made choice
  useEffect(() => {
    setSelected(null);
    setPending(null);
  }, [state.v]);

  useEffect(() => {
    if (myTurn && !prevMyTurn.current) haptic('soft');
    prevMyTurn.current = myTurn;
  }, [myTurn, turnKey]);

  useEffect(() => {
    if (!fx || !fx.cards?.length) return undefined;
    setGhost(fx);
    const id = setTimeout(() => setGhost(null), FX_MS);
    return () => clearTimeout(id);
  }, [fx]);

  useEffect(() => {
    const onResize = () => setWidth(Math.min(window.innerWidth, 480));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const deadlineAt = state.receivedAt && state.deadlineMs ? state.receivedAt + state.deadlineMs : null;
  const bitoPause = state.phase === 'BITO';
  const pauseEndsAt = bitoPause ? (state.receivedAt || Date.now()) + (state.pauseMs || 0) : null;

  const beat = (card, target) => {
    if (send({ t: 'durak.beat', card, target })) setPending(card);
    setSelected(null);
  };

  const onHandTap = (code) => {
    if (pending) return;
    if (!legal.has(code)) {
      haptic('rigid');
      return;
    }
    if (attacking) {
      if (send({ t: 'durak.play', card: code })) setPending(code);
      return;
    }
    if (defending) {
      const targets = beatTargets[code] || [];
      if (targets.length === 1) {
        beat(code, targets[0]);
      } else if (targets.length > 1) {
        hapticSelect();
        setSelected(selected === code ? null : code);
      }
    }
  };

  const onTableTap = (attackCode) => {
    if (!defending || !selected) return;
    if ((beatTargets[selected] || []).includes(attackCode)) beat(selected, attackCode);
  };

  const hand = state.hand || [];
  const cardW = width < 360 ? 58 : 66;
  const layout = fanLayout(hand.length, {
    width: width - 24, cardW, maxStep: cardW * 0.72, maxAngle: 3.5,
  });
  const selectedTargets = selected ? new Set(beatTargets[selected] || []) : null;
  const meFrom = (attackSide) => ((attackSide ? state.attacker : state.defender) === you ? 'dk-from-me' : 'dk-from-opp');

  let ghostClass = '';
  if (ghost) {
    if (ghost.kind === 'bito') ghostClass = 'dk-ghost--bito';
    else ghostClass = ghost.by === you ? 'dk-ghost--to-me' : 'dk-ghost--to-opp';
  }

  return (
    <div className="dk-table">
      <header className="dk-top">
        <button type="button" className="dk-icon-btn" onClick={onSurrender} aria-label={t('durak.surrender', 'Surrender')}>
          <svg viewBox="0 0 24 24"><path d="M15 5 8 12l7 7" /></svg>
        </button>
        <div className="dk-opp">
          <div className={`dk-opp__avatar${!myTurn && state.phase !== 'OVER' && !bitoPause ? ' is-turn' : ''}`}>
            {match?.opponent?.isBot ? '🤖' : '🃏'}
          </div>
          <div className="dk-opp__info">
            <div className="dk-opp__name">
              {match?.opponent?.name || t('brawl.opponent', 'Opponent')}
              {match?.opponent?.isBot && <span className="dk-tag">{t('durak.tag_bot', 'BOT')}</span>}
              {state.oppOnline === false && <span className="dk-tag dk-tag--off">{t('durak.tag_offline', 'offline')}</span>}
            </div>
            <div className="dk-opp__sub">
              {Number(match?.stake) > 0
                ? <Trans i18nKey="durak.pot" defaults="Pot <b>{{amount}}</b> GRAM" values={{ amount: formatGram(match.prize) }} components={{ b: <b /> }} />
                : t('durak.free_table', 'Free table')}
            </div>
          </div>
          {!myTurn && deadlineAt && <DeadlineRing deadlineAt={deadlineAt} totalMs={state.turnMs || 30000} />}
        </div>
      </header>

      <OpponentFan count={state.oppCount} />

      <section className="dk-felt">
        <Deck deckCount={state.deckCount} trump={state.trump} trumpSuit={state.trumpSuit} pulseKey={state.deckCount} />
        <div className={`dk-pairs${table.length > 3 ? ' dk-pairs--two-rows' : ''}${bitoPause ? ' is-bito' : ''}`} data-testid="table">
          {table.map((p) => {
            const isTarget = selectedTargets?.has(p.a);
            return (
              <button
                type="button"
                key={p.a}
                className={`dk-pair${isTarget ? ' is-target' : ''}${defending && !p.d ? ' is-open' : ''}`}
                onClick={() => onTableTap(p.a)}
                aria-label={p.d
                  ? t('durak.pair_beaten_aria', 'Attack {{a}}, beaten by {{d}}', { a: cardLabel(p.a), d: cardLabel(p.d) })
                  : t('durak.pair_aria', 'Attack {{a}}', { a: cardLabel(p.a) })}
                data-attack={p.a}
              >
                <span className={`dk-pair__a ${meFrom(true)}`}><PlayingCard code={p.a} /></span>
                {p.d && <span className={`dk-pair__d ${meFrom(false)}`}><PlayingCard code={p.d} /></span>}
              </button>
            );
          })}
          {table.length === 0 && !ghost && <div className="dk-felt__hint">{attacking ? t('durak.your_move', 'Your move') : ''}</div>}
          {bitoPause && table.length > 0 && <BitoBadge endsAt={pauseEndsAt} totalMs={state.bitoPauseMs || 3000} />}
          {ghost && (
            <div key={ghost.key} className={`dk-ghost ${ghostClass}`} aria-hidden="true">
              {ghost.cards.map((p) => (
                <span key={`g-${p.a}`} className="dk-pair dk-pair--ghost">
                  <span className="dk-pair__a"><PlayingCard code={p.a} /></span>
                  {p.d && <span className="dk-pair__d"><PlayingCard code={p.d} /></span>}
                </span>
              ))}
            </div>
          )}
        </div>
        <Discard count={state.discardCount} />
      </section>

      <div className={`dk-status${myTurn ? ' is-mine' : ''}`}>
        {myTurn && deadlineAt && <DeadlineRing deadlineAt={deadlineAt} totalMs={state.turnMs || 30000} mine />}
        <span className="dk-status__text">{statusText(t, state, you)}</span>
        {state.timeouts?.[you] > 0 && <span className="dk-status__warn">AFK {state.timeouts[you]}/3</span>}
      </div>

      <div className="dk-actions">
        {state.canTake && (
          <button type="button" className="app-btn app-btn--yellow dk-action" onClick={() => send({ t: 'durak.take' })}>
            {t('durak.take', 'Take')}
          </button>
        )}
        {state.canDone && (
          <button type="button" className="app-btn app-btn--green dk-action" onClick={() => send({ t: 'durak.done' })}>
            {state.taking ? t('durak.done', 'Done') : t('durak.done_bito', 'Done · Бито')}
          </button>
        )}
      </div>

      <div className="dk-hand" data-testid="hand" style={{ height: cardW * 1.4 + 26 }}>
        {hand.map((code, i) => {
          const p = layout[i];
          const isLegal = legal.has(code);
          const cls = [
            'dk-hand__card',
            myTurn && isLegal ? 'is-legal' : '',
            myTurn && !isLegal ? 'is-dim' : '',
            selected === code ? 'is-selected' : '',
            pending === code ? 'is-pending' : '',
          ].filter(Boolean).join(' ');
          const lift = (myTurn && isLegal ? 14 : 0) + (selected === code ? 16 : 0);
          return (
            <button
              type="button"
              key={code}
              className={cls}
              data-card={code}
              aria-label={cardLabel(code)}
              onClick={() => onHandTap(code)}
              style={{
                width: cardW,
                marginLeft: -cardW / 2,
                transform: `translateX(${p.x}px) translateY(${p.y - lift}px) rotate(${p.rot}deg)`,
                zIndex: i + 1,
              }}
            >
              <PlayingCard code={code} />
            </button>
          );
        })}
      </div>

      {(connection === 'reconnecting' || connection === 'connecting') && (
        <div className="dk-banner">{t('pvp.reconnecting', 'Reconnecting…')}</div>
      )}
      {error && error.code !== 'CONNECTION' && <div key={error.at} className="dk-toast">{gameErrorText(t, error)}</div>}
    </div>
  );
}
